import React, { useState, useEffect } from 'react';
import { Container, Box, Tabs, Tab, Typography, TextField, Button } from '@mui/material';
import Header from '../components/Header';
import SnackbarAlert from '../components/SnackbarAlert';
import FileUpload from '../components/FileUpload';
import TabContent from '../components/TabContent';
import axios from 'axios';
import '../App.css';
import { useNavigate } from 'react-router-dom';


function CV_Wizard() {
    const navigate = useNavigate();
    const [file, setFile] = useState(null);
    const [driveLink, setDriveLink] = useState('');
    const [processing, setProcessing] = useState(false);
    const [grade, setGrade] = useState('--');
    const [status, setStatus] = useState('');
    const [fileName, setFileName] = useState(''); // State for file name
    const [comments, setComments] = useState({
        work: [],
        education: [],
        details: [],
    });
    const [additionalRecommendations, setAdditionalRecommendations] = useState(`
        Formatting Errors: Use a clean, professional layout, consistent fonts, and a well-organized structure.
        Missing Relevant Skills: List only relevant tools, languages, and frameworks.
        Irrelevant Details: Only include experience and education relevant to the role.
        Generic CV Submissions: Customize the CV for each job by including job-specific keywords.
        Missing/Incorrect Contact Information: Ensure all contact details are accurate and professional.
    `);
    

    const [tabIndex, setTabIndex] = useState(0);
    const [error, setError] = useState('');
    const [showError, setShowError] = useState(false);

    useEffect(() => {
        if (grade.startsWith('00')) {
            setFile(null);
            setDriveLink('');
            setProcessing(false);
            setStatus('');
            setFileName(''); // Clear file name on error
            setComments({
                work: [],
                education: [],
                details: [],
            });
        }
    }, [grade]);

    const handleError = (message) => {
        setError(message);
        setShowError(true);
    };

    const onDrop = async (acceptedFiles) => {
        const uploadedFile = acceptedFiles[0];
        if (!uploadedFile) {
            handleError('File format not supported. Please upload a PDF or Word file.');
            return;
        }

        if (uploadedFile.size > 2097152) {
            handleError('File size exceeds 2MB limit.');
            return;
        }

        await sendFileToBackend(uploadedFile);
    };

    const handleDriveLinkSubmit = async () => {
        if (!driveLink) {
            handleError('Please enter a valid drive link.');
            return;
        }

        await sendUrlToBackend(driveLink);
    };

    const sendFileToBackend = async (uploadedFile) => {
        setProcessing(true);
        setGrade('--');
        setStatus('');
        setFileName(''); // Clear file name before processing
        setComments({
            work: [],
            education: [],
            details: [],
        });

        const formData = new FormData();
        formData.append('file', uploadedFile);

        try {
            const response = await axios.post('api/upload_pdf', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            const { comments: receivedComments, name } = response.data;
            setGrade(receivedComments.grade || '--');
            setStatus(receivedComments.status || '');
            setFileName(receivedComments.name|| ''); // Set file name
            setComments({
                work: receivedComments.work || ['No comments on work experience.'],
                education: receivedComments.education || ['No comments on education.'],
                details: receivedComments.details || ['No general comments available.'],
            });
        } catch (error) {
            handleError('There was an error analyzing the file. Please try again.');
        } finally {
            setProcessing(false);
        }
    };

    const sendUrlToBackend = async (driveLink) => {
        setProcessing(true);
        setGrade('--');
        setStatus('');
        setFileName(''); // Clear file name before processing
        setComments({
            work: [],
            education: [],
            details: [],
        });

        try {
            const response = await axios.post(
                'api/upload_url',
                { driveLink },
                { headers: { 'Content-Type': 'application/json' } }
            );

            const { comments: receivedComments, name } = response.data;
            setGrade(receivedComments.grade || '--');
            setStatus(receivedComments.status || '');
            setFileName(receivedComments.name || ''); // Set file name
            setComments({
                work: receivedComments.work || ['No comments on work experience.'],
                education: receivedComments.education || ['No comments on education.'],
                details: receivedComments.details || ['No general comments available.'],
            });
        } catch (error) {
            handleError('There was an error analyzing the URL. Please try again.');
        } finally {
            setProcessing(false);
        }
    };

    const handleTabChange = (event, newValue) => {
        setTabIndex(newValue);
    };

    const handleCloseError = () => {
        setShowError(false);
    };

    const getMessageLine = () => {
        if (grade.startsWith('00')) {
            return (
                <Typography
                    style={{
                        color: 'red',
                        fontFamily: 'Space Mono',
                        textAlign: 'center',
                        fontWeight: 'bold',
                    }}
                >
                    The provided file doesn't appear to be a CV. Please upload a CV for evaluation.
                </Typography>
            );
        }
        if (parseInt(grade, 10) < 70 && grade !== '--') {
            return (
                <Typography
                    style={{
                        color: 'white',
                        fontFamily: 'Space Mono',
                        textAlign: 'center',
                    }}
                >
                    Don't get discouraged! Follow our detailed comments and you'll have the perfect CV!
                </Typography>
            );
        }
        return null;
    };

    return (
        <Container>
        <Header title="CV Wizard" />

        {/* Back Button */}
        <Box mt={-4} mb={2}>
            <Button
                variant="contained"
                onClick={() => navigate('/')} // Navigate back to HomePage
                style={{
                    fontFamily: 'Space Mono',
                    color: 'black', // Black text
                    fontSize: '16px',
                    textTransform: 'none',
                    backgroundColor: 'rgb(162, 194, 78)', // Matching green background
                    padding: '4px 16px', // Add padding for better button size
                    borderRadius: '8px', // Rounded corners
                }}
            >
                ← Back
            </Button>
        </Box>

        {/* Description Text */}
        <Typography
            variant="h7"
            style={{
                fontFamily: 'Space Mono',
                color: 'white',
                marginBottom: '10px',
                maxWidth: '1200px', // Limit width for readability
            }}
        >
            Upload your CV below or use a Google Drive URL for evaluation. Make sure it's available to the public if on drive.
        </Typography>

        <SnackbarAlert error={error} showError={showError} handleCloseError={handleCloseError} />
    
            <Box display="flex" alignItems="center" justifyContent="space-between" mt={4}>
                <Box style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '16px', maxWidth: '600px' }}>
                    <FileUpload onDrop={onDrop} processing={processing} />
                    <Box
    style={{
        flex: 1,
        padding: 16,
        backgroundColor: 'rgb(162, 194, 78)',
        textAlign: 'center',
        height: 50,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderRadius: 8,
        maxWidth: '513px',
    }}
>
    <form
        onSubmit={(e) => {
            e.preventDefault(); // Prevent default form submission
            handleDriveLinkSubmit(); // Call the submit function
        }}
        style={{ display: 'flex', width: '100%' }} // Adjust form style
    >
        <TextField
            placeholder="Enter a URL for your CV (PDF, Word, Max 2MB)"
            value={driveLink}
            onChange={(e) => setDriveLink(e.target.value)}
            fullWidth
            size="small"
            InputProps={{
                style: {
                    color: 'black',
                },
            }}
            style={{
                backgroundColor: 'rgb(211, 211, 211)',
                borderRadius: 8,
                marginRight: 8,
            }}
        />
        <Button
            type="submit" // Ensures button works with the form
            variant="contained"
            color="primary"
            style={{
                fontFamily: 'Space Mono',
                fontSize: '14px',
                padding: '8px 16px',
                borderRadius: 8,
                backgroundColor: 'rgb(211, 211, 211)',
                color: 'black',
            }}
        >
            Submit
        </Button>
    </form>
</Box>
                </Box>

                <Box
                    style={{
                        flex: 1,
                        padding: 16,
                        backgroundColor:
                            status.toLowerCase() === 'passed'
                                ? 'rgb(162, 194, 78)'
                                : status.toLowerCase() === 'failed'
                                ? 'rgb(255, 99, 71)'
                                : 'rgb(211, 211, 211)',
                        height: 140,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: 8,
                    }}
                >
                    <Typography variant="h4" style={{ color: 'black', fontFamily: 'Space Mono' }}>
                        Grade:
                    </Typography>
                    <Typography variant="h5" style={{ color: 'black', fontFamily: 'Space Mono' }}>
                        {grade} {status && `| ${status.toUpperCase()}`}
                    </Typography>
                </Box>
            </Box>

            {fileName && (
                <Box mt={2} textAlign="center">
                    <Typography
                        style={{
                            color: 'white',
                            fontFamily: 'Space Mono',
                            textAlign: 'center',
                        }}
                    >
                        File Name: {fileName}
                    </Typography>
                </Box>
            )}

            {getMessageLine() && (
                <Box mt={2} mb={2}>
                    {getMessageLine()}
                </Box>
            )}

<Tabs value={tabIndex} onChange={handleTabChange} indicatorColor="primary" textColor="primary" style={{ marginBottom: 16 }}>
                <Tab label="General Comments" style={{ color: 'white', fontFamily: 'Space Mono' }} />
                <Tab label="Work Experience" style={{ color: 'white', fontFamily: 'Space Mono' }} />
                <Tab label="Education" style={{ color: 'white', fontFamily: 'Space Mono' }} />
                <Tab label="Common Mistakes to Avoid" style={{ color: 'white', fontFamily: 'Space Mono' }} />
                <Tab label="AI Certification Experience" style={{ color: 'white', fontFamily: 'Space Mono' }} />
            </Tabs>

            {tabIndex !== 3 && tabIndex !== 4 && <TabContent tabIndex={tabIndex} comments={comments} />}

            {tabIndex === 3 && (
                <Box style={{ color: 'white', fontFamily: 'Space Mono' }}>
                    <Typography
                        variant="h6"
                        style={{
                            marginBottom: '8px',
                            fontWeight: 'bold',
                        }}
                    >
                        Common Mistakes to Avoid:
                    </Typography>
                    <ul
                        style={{
                            listStyleType: 'disc',
                            paddingLeft: '20px',
                            lineHeight: '1.8',
                            color: 'white',
                        }}
                    >
                        {additionalRecommendations
                            .trim()
                            .split('\n')
                            .map((line, index) => {
                                const [title, ...rest] = line.split(':'); // Split into title and rest
                                return (
                                    <li key={index} style={{ marginBottom: '8px', color: 'white' }}>
                                        <span style={{ fontWeight: 'bold' }}>{title.trim()}:</span>{' '}
                                        <span>{rest.join(':').trim()}</span> {/* Join rest in case there's more than one colon */}
                                    </li>
                                );
                            })}
                    </ul>
                </Box>
            )}

{tabIndex === 4 && (
    <Box>
        <Typography
            variant="body1"
            style={{
                marginBottom: '20px',
                color: 'white',
                fontFamily: 'Space Mono',
            }}
        >
            Follow these templates to correctly showcase your ZAKA certification experience on your CV:
        </Typography>
        <Box
            style={{
                marginBottom: '40px',
                color: 'white',
                fontFamily: 'Space Mono',
                textAlign: 'justify',
                lineHeight: '1.8',
                maxWidth: '1200px', // Restrict the width of the text
            }}
        >
            {/* Data Analytics Specialization */}
            <Typography
                variant="h5"
                style={{
                    fontWeight: 'bold',
                    textDecoration: 'underline',
                    marginBottom: '12px',
                    color: 'rgb(162, 194, 78)',

                }}
            >
                Data Analytics Specialization
            </Typography>
            <Typography variant="body1" style={{ marginBottom: '16px' }}>
                Completed the 20-week Data Analytics Specialization training program to prepare for a Data Analyst position. The program covered the following topics: data collection and cleaning, exploratory data analysis, statistical analysis, data visualization, SQL, Tableau, Excel, Power BI, and data-driven decision making.
            </Typography>
            <Typography variant="body1" style={{ marginBottom: '16px' }}>
    The project can be added in the Project or Experience section in the following format:
</Typography>
            <Typography
                variant="subtitle1"
                style={{
                    fontWeight: 'bold',
                    textDecoration: 'underline',
                    marginBottom: '8px',
                }}
            >
                Projects Section:
            </Typography>
            <Typography variant="body1" style={{ marginBottom: '16px' }}>
                Name of the project, Data Analytics Specialization  
                Collected / Analyzed / Visualized (Explain what you did)
            </Typography>
            <Typography
                variant="subtitle1"
                style={{
                    fontWeight: 'bold',
                    textDecoration: 'underline',
                    marginBottom: '8px',
                }}
            >
                Experience Section:
            </Typography>
            <Typography variant="body1" style={{ marginBottom: '16px' }}>
                Data Analyst Trainee, Company Name / ZAKA  
                Collected / Analyzed / Visualized (Explain what you did)
            </Typography>

            {/* Data Science Specialization */}
            <Typography
                variant="h5"
                style={{
                    fontWeight: 'bold',
                    textDecoration: 'underline',
                    marginBottom: '12px',
                    color: 'rgb(162, 194, 78)',

                }}
            >
                Data Science Specialization
            </Typography>
            <Typography variant="body1" style={{ marginBottom: '16px' }}>
                Completed the 20-week Data Science Specialization training program to prepare for a Data Scientist position. The program covered the following topics: Data Science fundamentals, statistical analysis, machine learning algorithms, data visualization, big data technologies, and data-driven decision making.
            </Typography>
            <Typography variant="body1" style={{ marginBottom: '16px' }}>
    The project can be added in the Project or Experience section in the following format:
</Typography>
            <Typography
                variant="subtitle1"
                style={{
                    fontWeight: 'bold',
                    textDecoration: 'underline',
                    marginBottom: '8px',
                }}
            >
                Projects Section:
            </Typography>
            <Typography variant="body1" style={{ marginBottom: '16px' }}>
                Name of the project, Data Science Specialization  
                Built / Implemented / Analyzed (Explain what you did)
            </Typography>
            <Typography
                variant="subtitle1"
                style={{
                    fontWeight: 'bold',
                    textDecoration: 'underline',
                    marginBottom: '8px',
                }}
            >
                Experience Section:
            </Typography>
            <Typography variant="body1" style={{ marginBottom: '16px' }}>
                Data Science Trainee, Company Name / ZAKA  
                Built / Implemented / Analyzed (Explain what you did)
            </Typography>

            {/* Machine Learning Specialization */}
            <Typography
                variant="h5"
                style={{
                    fontWeight: 'bold',
                    textDecoration: 'underline',
                    marginBottom: '12px',
                    color: 'rgb(162, 194, 78)',

                }}
            >
                Machine Learning Specialization
            </Typography>
            <Typography variant="body1" style={{ marginBottom: '16px' }}>
    Completed the 20-week Machine Learning Specialization training program to prepare for a Machine Learning Engineer position. The program covered the following topics: fundamentals of Data Science, Machine Learning algorithms, Deep Learning, Computer Vision, Natural Language Processing, ML in Production, and ML in Research.
</Typography>
<Typography variant="body1" style={{ marginBottom: '16px' }}>
    The project can be added in the Project or Experience section in the following format:
</Typography>

            <Typography
                variant="subtitle1"
                style={{
                    fontWeight: 'bold',
                    textDecoration: 'underline',
                    marginBottom: '8px',
                }}
            >
                Projects Section:
            </Typography>
            <Typography variant="body1" style={{ marginBottom: '16px' }}>
                Name of the project, Machine Learning Specialization  
                Built / Implemented / Deployed (Explain what you did)
            </Typography>
            <Typography
                variant="subtitle1"
                style={{
                    fontWeight: 'bold',
                    textDecoration: 'underline',
                    marginBottom: '8px',
                }}
            >
                Experience Section:
            </Typography>
            <Typography variant="body1" style={{ marginBottom: '16px' }}>
                Machine Learning Engineer Trainee, Company Name / ZAKA  
                Built / Implemented / Deployed (Explain what you did)
            </Typography>
        </Box>
    </Box>
)}


        </Container>
    );
}

export default CV_Wizard;
